import React from 'react';

import { faCalendar } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Popover, PopoverContent, PopoverTrigger, PopoverPortal } from '@radix-ui/react-popover';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { parkingManagementMessages } from 'src/features/parkingManagement/utils';
import { useListPoints } from 'src/features/points/api/useListPoints';
import { reservationMessages } from 'src/features/reservations/utils/messages';
import { Button } from 'src/registry/ui/Button';
import { Calendar } from 'src/registry/ui/Calendar';
import { FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';
import {
    MultiSelect,
    MultiSelectContent,
    MultiSelectControl,
    MultiSelectOption,
    MultiSelectTrigger,
} from 'src/registry/ui/MultiSelect';
import { Textarea } from 'src/registry/ui/Textarea';
import { generalMessages } from 'src/utils/messages';

type ReservationNameTypeDateProps = {
    selectedFromDate: Date | undefined;
    setSelectedFromDate: (date: Date | undefined) => void;
    selectedToDate: Date | undefined;
    setSelectedToDate: (date: Date | undefined) => void;
    showValidToDate?: boolean;
};

const ReservationNameTypeDate = ({
    selectedFromDate,
    setSelectedFromDate,
    selectedToDate,
    setSelectedToDate,
    showValidToDate,
}: ReservationNameTypeDateProps) => {
    const { control, setValue } = useFormContext();
    const { formatMessage, formatDate } = useIntl();
    const [openFromCalendar, setOpenFromCalendar] = React.useState<boolean>(false);
    const [openToCalendar, setOpenToCalendar] = React.useState<boolean>(false);
    const { parkingId } = useParams();
    const { data: pointsData } = useListPoints({ parkingId: parkingId ?? '', config: { enabled: !!parkingId } });
    const allPoints = pointsData?.results ?? [];

    const selectedDateDisplay = (date?: Date) => {
        const today = new Date();

        if (date) {
            return date && date < today ? formatDate(today) : formatDate(date);
        } else {
            return date ? formatDate(date) : formatMessage(generalMessages.pickADate);
        }
    };

    return (
        <>
            <FormField
                control={control}
                name="name"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            {formatMessage(reservationMessages.name)}
                        </FormLabel>
                        <div className="flex w-full gap-4">
                            <FormControlStack>
                                <FormControl>
                                    <Input
                                        type="text"
                                        placeholder={formatMessage(reservationMessages.versionName)}
                                        {...field}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </div>
                    </FormItem>
                )}
            />
            <FormField
                control={control}
                name="description"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">{formatMessage(reservationMessages.description)}</FormLabel>
                        <div className="flex w-full gap-4">
                            <FormControlStack>
                                <FormControl>
                                    <Textarea
                                        placeholder={formatMessage(reservationMessages.versionDescription)}
                                        {...field}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </div>
                    </FormItem>
                )}
            />
            <FormField
                control={control}
                name="points"
                render={({ field }) => {
                    const { ref: _, ...rest } = field;

                    const fieldData = field.value
                        ?.map((point) => allPoints?.find((p) => p.id === point)?.name)
                        .join(', ');

                    const allValues = allPoints.map((point) => point.id);

                    return (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog">
                                <FormattedMessage {...parkingManagementMessages.pointsGroupPoints} />
                            </FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <MultiSelect {...rest}>
                                        <MultiSelectTrigger asChild>
                                            <MultiSelectControl className="w-full">
                                                {fieldData?.length > 0 ? (
                                                    fieldData
                                                ) : (
                                                    <span className="text-gray-400">
                                                        <FormattedMessage
                                                            {...parkingManagementMessages.pointsGroupPoints}
                                                        />
                                                    </span>
                                                )}
                                            </MultiSelectControl>
                                        </MultiSelectTrigger>
                                        <MultiSelectContent allValues={allValues}>
                                            {allPoints?.map((point) => {
                                                return (
                                                    <MultiSelectOption key={point.id} selected={point.id}>
                                                        {point.name}
                                                    </MultiSelectOption>
                                                );
                                            })}
                                        </MultiSelectContent>
                                    </MultiSelect>
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    );
                }}
            />
            <FormField
                control={control}
                name="valid_from"
                render={() => {
                    return (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog" required>
                                {formatMessage(reservationMessages.reservationValidFrom)}
                            </FormLabel>
                            <Popover open={openFromCalendar} onOpenChange={setOpenFromCalendar}>
                                <PopoverTrigger asChild>
                                    <Button
                                        id="date"
                                        variant="outline"
                                        className="text-base font-normal"
                                        onClick={() => setOpenFromCalendar(true)}
                                    >
                                        <FontAwesomeIcon icon={faCalendar} className="text-primary-700 mr-1 size-4" />
                                        {selectedDateDisplay(selectedFromDate)}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverPortal>
                                    <PopoverContent className="z-50 bg-white p-4 shadow-sm" align="end">
                                        <Calendar
                                            mode="single"
                                            selected={selectedFromDate}
                                            onSelect={(date) => {
                                                if (date) {
                                                    setSelectedFromDate(date);
                                                    setValue('valid_from', date, {
                                                        shouldValidate: true,
                                                        shouldDirty: true,
                                                    });
                                                    setOpenFromCalendar(!openFromCalendar);
                                                }
                                            }}
                                            disabled={{ before: new Date() }}
                                        />
                                    </PopoverContent>
                                </PopoverPortal>
                            </Popover>
                        </FormItem>
                    );
                }}
            />
            {showValidToDate && (
                <FormField
                    control={control}
                    name="valid_to"
                    render={() => {
                        return (
                            <FormItem variant="dialog">
                                <FormLabel variant="dialog" required>
                                    {formatMessage(reservationMessages.reservationValidTo)}
                                </FormLabel>
                                <Popover open={openToCalendar} onOpenChange={setOpenToCalendar}>
                                    <PopoverTrigger asChild>
                                        <Button
                                            id="date"
                                            variant="outline"
                                            className="text-base font-normal"
                                            onClick={() => setOpenToCalendar(true)}
                                        >
                                            <FontAwesomeIcon
                                                icon={faCalendar}
                                                className="text-primary-700 mr-1 size-4"
                                            />
                                            {selectedDateDisplay(selectedToDate)}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverPortal>
                                        <PopoverContent className="z-50 bg-white p-4 shadow-sm" align="end">
                                            <Calendar
                                                mode="single"
                                                selected={selectedToDate}
                                                onSelect={(date) => {
                                                    if (date) {
                                                        setSelectedToDate(date);
                                                        setValue('valid_to', date, {
                                                            shouldValidate: true,
                                                            shouldDirty: true,
                                                        });
                                                        setOpenToCalendar(!openToCalendar);
                                                    }
                                                }}
                                                disabled={{ before: new Date() }}
                                            />
                                        </PopoverContent>
                                    </PopoverPortal>
                                </Popover>
                            </FormItem>
                        );
                    }}
                />
            )}
        </>
    );
};

export default ReservationNameTypeDate;
